import { useEstateContext } from "@/context/EstateContext";
import { useLanguage } from "@/context/LanguageContext";
import { formatCZK } from "@/utils/MyEstateHelper";
import React from "react";
import { SectionBadge } from "@/components/hlidaci-pes/chart-line-interactive/Shared";
import { StatusPill } from "@/components/portfolio/StatusPill";
import {
  DataGrid,
  DataGridCell,
  DataGridHeader,
  DataGridLabel,
  DataGridRow,
} from "@/components/ui/DataGrid";

const TAX_RATE = 0.15;
const PAUSALNI_EXPENSE_RATIO = 0.3;
const DEPRECIATION_YEARS = 30;

const SectionDivider = () => (
  <div className="grid grid-cols-3 gap-3 px-1 my-1">
    <div />
    <div className="h-px bg-gray-100" />
    <div className="h-px bg-gray-100" />
  </div>
);

interface RowProps {
  label: string;
  pausalni: React.ReactNode;
  skutecne: React.ReactNode;
  text?: string;
  bold?: boolean;
  highlight?: boolean;
  hover?: boolean;
}
const Row = ({
  label,
  pausalni,
  skutecne,
  text,
  bold = false,
  highlight = false,
  hover = false,
}: RowProps) => (
  <DataGridRow
    interactive={false}
    className={`grid-cols-3 gap-3 rounded-lg px-1 py-1.5 text-center transition-colors
        ${highlight ? "bg-softiq-secondary/5" : ""}
        ${bold ? "font-semibold" : ""}
        ${hover ? "text-gray-400" : "text-foreground"}
    `}
  >
    <DataGridCell className="text-left">
      <span>{label}</span>
      {text && <p className="text-xs text-gray-400">{text}</p>}
    </DataGridCell>
    <DataGridCell>{pausalni}</DataGridCell>
    <DataGridCell>{skutecne}</DataGridCell>
  </DataGridRow>
);

export const TaxOverview = () => {
  const { language } = useLanguage();
  const copy = {
    cs: {
      taxOverview: "Daňový přehled",
      flatRate: "Paušální",
      actual: "Skutečné",
      income: "Příjmy",
      expenses: "Výdaje",
      depreciation: "Odpisy",
      appraisalText:
        "Hodnotu nemovitosti musíte doložit odborným odhadem. Uvažovaná hodnota nemovitosti: {value}",
      taxBase: "Základ daně",
      tax: "Daň ({rate} %)",
      taxDifference: "Rozdíl na dani",
      save: "ušetříte",
      withActual: "se skutečnými výdaji",
      withFlatRate: "s paušálem",
      actualBetter: "Skutečné výdaje výhodnější",
      flatRateBetter: "Paušální daň výhodnější",
    },
    en: {
      taxOverview: "Tax overview",
      flatRate: "Flat-rate",
      actual: "Actual",
      income: "Income",
      expenses: "Expenses",
      depreciation: "Depreciation",
      appraisalText:
        "You must document the property value with a professional appraisal. Considered property value: {value}",
      taxBase: "Tax base",
      tax: "Tax ({rate}%)",
      taxDifference: "Tax difference",
      save: "you save",
      withActual: "with actual expenses",
      withFlatRate: "with flat-rate expenses",
      actualBetter: "Actual expenses are better",
      flatRateBetter: "Flat-rate tax is better",
    },
  }[language];
  const { income, expense, selectedEstate, analysis, isTaxView } =
    useEstateContext();

  if (!selectedEstate) return null;
  if (!isTaxView) return null;

  const inc = Number(income);
  const exp = Number(expense);
  const estateAge =
    new Date().getFullYear() - new Date(selectedEstate.dateFrom).getFullYear();
  const useAnalysis = estateAge > 5;
  const purchasePrice = useAnalysis
    ? Number(analysis.avgPriceSell)
    : Number(selectedEstate.purchasePrice);

  const depreciation = estateAge < 31 ? purchasePrice / DEPRECIATION_YEARS : 0;
  // Paušální
  const p_expense = inc * PAUSALNI_EXPENSE_RATIO;
  const p_odpisy = 0;
  const p_profit = inc - p_expense - p_odpisy;
  const p_taxBase = Math.max(0, p_profit);
  const p_tax = p_taxBase * TAX_RATE;

  // Skutečné
  const s_expense = exp;
  const s_odpisy = depreciation;
  const s_profit = inc - s_expense - s_odpisy;
  const s_taxBase = Math.max(0, s_profit);
  const s_tax = s_taxBase * TAX_RATE;

  const diff = p_tax - s_tax;
  const skutecneBetter = s_tax <= p_tax;

  return (
    <>
      <SectionBadge>{copy.taxOverview}</SectionBadge>

      {/* Column Headers */}
      <DataGrid>
        <DataGridHeader className="mb-1 grid-cols-3 gap-3 border-0 bg-transparent px-1 dark:bg-transparent">
          <span />
          {[copy.flatRate, copy.actual].map((h) => (
            <div key={h} className="text-center">
              <DataGridLabel className="text-sm normal-case tracking-wide">
                {h}
              </DataGridLabel>
              <div className="h-px bg-softiq-secondary mt-1" />
            </div>
          ))}
        </DataGridHeader>

        <Row
          label={copy.income}
          pausalni={formatCZK(inc)}
          skutecne={formatCZK(inc)}
        />
        <Row
          label={copy.expenses}
          pausalni={formatCZK(p_expense)}
          skutecne={formatCZK(s_expense)}
        />
        <Row
          label={copy.depreciation}
          pausalni={formatCZK(p_odpisy)}
          skutecne={formatCZK(s_odpisy)}
          text={
            useAnalysis
              ? copy.appraisalText.replace(
                  "{value}",
                  formatCZK(analysis.avgPriceSell),
                )
              : ""
          }
        />

        <SectionDivider />

        <Row
          label={copy.taxBase}
          pausalni={formatCZK(p_taxBase)}
          skutecne={formatCZK(s_taxBase)}
          bold
        />

        <SectionDivider />

        <Row
          label={copy.tax.replace("{rate}", String(TAX_RATE * 100))}
          pausalni={
            <span
              className={!skutecneBetter ? "text-green-600 font-semibold" : ""}
            >
              {formatCZK(p_tax)}
            </span>
          }
          skutecne={
            <span
              className={skutecneBetter ? "text-green-600 font-semibold" : ""}
            >
              {formatCZK(s_tax)}
            </span>
          }
          highlight
          bold
        />

        {Math.abs(diff) > 0 && (
          <div className="mt-4 pt-3 border-t border-dashed border-gray-200 flex items-center justify-between text-xs text-gray-400">
            <span>{copy.taxDifference}</span>
            <span className={`font-semibold text-sm text-softiq-secondary`}>
              {copy.save} {formatCZK(Math.abs(diff))}
              <span className="font-normal text-gray-400 ml-1">
                {skutecneBetter ? copy.withActual : copy.withFlatRate}
              </span>
            </span>
          </div>
        )}
        {Math.abs(diff) > 0 && (
          <StatusPill>
            {skutecneBetter ? copy.actualBetter : copy.flatRateBetter}
          </StatusPill>
        )}
      </DataGrid>
    </>
  );
};
